import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';

// Service abbreviation mapping (similar to NAPPS)
const SERVICE_ABBREV = {
  'process_serving': { abbrev: 'PS', name: 'Process Serving', color: 'bg-teal-500/20 text-teal-400 border-teal-500/30' },
  'notary': { abbrev: 'N', name: 'Notary Public', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  'mobile_notary': { abbrev: 'MN', name: 'Mobile Notary', color: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' },
  'private_investigator': { abbrev: 'PI', name: 'Private Investigator', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  'court_filings': { abbrev: 'CF', name: 'Court Filings', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  'court_records_search': { abbrev: 'CRS', name: 'Court Records Search', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  'subpoena_preparation': { abbrev: 'SP', name: 'Subpoena Preparation', color: 'bg-sky-500/20 text-sky-400 border-sky-500/30' },
  'skip_tracing': { abbrev: 'ST', name: 'Skip Tracing', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' }
};

// Compact badges for search results
export function ServiceBadgesCompact({ services, maxDisplay = 4 }) {
  if (!services || services.length === 0) return null;
  
  // Services requiring verification - only show if verified
  const REQUIRES_VERIFICATION = ['notary', 'mobile_notary', 'private_investigator'];
  
  const displayServices = services.filter(s => {
    if (!s.enabled) return false;
    if (REQUIRES_VERIFICATION.includes(s.service_type)) {
      return s.verification_status === 'verified';
    }
    return true;
  });
  
  if (displayServices.length === 0) return null;
  
  const visibleServices = displayServices.slice(0, maxDisplay);
  const remaining = displayServices.length - maxDisplay;
  
  return (
    <div className="flex flex-wrap gap-1">
      {visibleServices.map(service => {
        const info = SERVICE_ABBREV[service.service_type];
        if (!info) return null;
        return (
          <span 
            key={service.service_type}
            className={`px-1.5 py-0.5 text-xs font-medium rounded border ${info.color}`}
            title={info.name}
          >
            {info.abbrev}
          </span>
        );
      })}
      {remaining > 0 && (
        <span className="px-1.5 py-0.5 text-xs text-slate-500">+{remaining}</span>
      )}
    </div>
  );
}

// Full service list with prices for profile page
export function ServicesList({ services, showPrices = true }) {
  if (!services || services.length === 0) return null;
  
  const enabledServices = services.filter(s => s.enabled);
  if (enabledServices.length === 0) return null;
  
  return (
    <div className="space-y-2">
      {enabledServices.map(service => {
        const info = SERVICE_ABBREV[service.service_type];
        if (!info) return null;
        return (
          <div 
            key={service.service_type}
            className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/40 border border-slate-700/50"
          >
            <div className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs font-bold rounded border ${info.color}`}>
                {info.abbrev}
              </span>
              <span className="text-sm text-slate-300">{info.name}</span>
            </div>
            {showPrices && service.price != null && (
              <span className="text-sm font-mono font-medium text-white">${service.price}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Service legend/key component
export function ServiceLegend({ compact = false }) {
  const [showLegend, setShowLegend] = useState(false);
  
  if (compact) {
    return (
      <div className="relative inline-block">
        <button 
          onClick={() => setShowLegend(!showLegend)}
          className="p-1 text-slate-500 hover:text-slate-300 transition-colors"
          title="Service Key"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        {showLegend && (
          <div className="absolute z-50 right-0 mt-1 p-3 bg-slate-900 border border-slate-700 rounded-lg shadow-xl min-w-[200px]">
            <p className="text-xs font-semibold text-white mb-2 uppercase tracking-wider">Service Key</p>
            <div className="space-y-1.5">
              {Object.entries(SERVICE_ABBREV).map(([key, info]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 font-bold rounded border ${info.color}`}>
                    {info.abbrev}
                  </span>
                  <span className="text-slate-400">{info.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  return (
    <div className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
      <p className="text-xs font-semibold text-white mb-3 uppercase tracking-wider">Service Key</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        {Object.entries(SERVICE_ABBREV).map(([key, info]) => (
          <div key={key} className="flex items-center gap-2 text-xs">
            <span className={`px-1.5 py-0.5 font-bold rounded border ${info.color}`}>
              {info.abbrev}
            </span>
            <span className="text-slate-400">{info.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default { ServiceBadgesCompact, ServicesList, ServiceLegend };
